import React, { useContext, useEffect } from 'react';
import { ProductContext } from '../../ProductProvider';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import "../../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { RoutesLocal } from '../../routes.js';

export default function Sidebar() {
  const { isSidebarOpen, setLoggedUser, loggedUser } = useContext(ProductContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const userDisplayName = loggedUser?.name || loggedUser?.email || 'Admin';
  const userInitials = userDisplayName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AD';

  const navigationSections = [
    {
      id: 'clients-nav',
      title: 'Registration',
      icon: 'bi-person-badge',
      links: [
        { label: 'New Registration', path: RoutesLocal.NewRegistration.path },
        { label: 'Manage Registration', path: RoutesLocal.ManageRegistration.path },
      ],
    },
    {
      id: 'reports-nav',
      title: 'Member',
      icon: 'bi-people',
      links: [
        { label: 'Search Members', path: RoutesLocal.MemberSearch.path },
        { label: 'Login Accounts', path: RoutesLocal.memberAccounts.path },
      ],
    },
    {
      id: 'catalogue-nav',
      title: 'Catalogue',
      icon: 'bi-journal-text',
      links: [
        { label: 'Create Catalogue', path: RoutesLocal.NewCatalogue.path },
        { label: 'Search Catalogue', path: RoutesLocal.SearchCatalogue.path },
      ],
    },
    {
      id: 'subscription-nav',
      title: 'Subscription',
      icon: 'bi-credit-card-2-front',
      links: [
        { label: 'New Subscription', path: RoutesLocal.AddSubscription.path },
        { label: 'Search Subscription', path: RoutesLocal.SearchSubscription.path },
      ],
    },
    {
      id: 'catreq-nav',
      title: 'Catalogue Request',
      icon: 'bi-inbox',
      links: [
        { label: 'New Request', path: RoutesLocal.NewRequestCatalogue.path },
        { label: 'Pending Requests', path: RoutesLocal.PendingRequestCatalogue.path },
        { label: 'Search Requests', path: RoutesLocal.SearchRequestCatalogue.path },
      ],
    },
    {
      id: 'website-nav',
      title: 'Website',
      icon: 'bi-window-stack',
      links: [
        { label: 'Slider', path: RoutesLocal.SliderPath.path },
        { label: 'Gallery', path: RoutesLocal.GalleryPath.path },
        { label: 'Info Board', path: RoutesLocal.FlashNews.path },
      ],
    },
  ];

  const isSectionActive = (section) =>
    section.links.some((link) => location.pathname === link.path || location.pathname.startsWith(`${link.path}/`));

  useEffect(() => {
    try {
      if (isSidebarOpen) {
        document.body.classList.add('toggle-sidebar');
      } else {
        document.body.classList.remove('toggle-sidebar');
      }
    } catch (err) {
      console.warn('Unable to toggle body class for sidebar:', err);
    }

    return () => {
      try {
        document.body.classList.remove('toggle-sidebar');
      } catch (err) {
        // ignore cleanup issues
      }
    };
  }, [isSidebarOpen]);

  const handleSignOut = async (event) => {
    event.preventDefault();

    try {
      await signOut();
      setLoggedUser({
        ...loggedUser,
        name: '',
        email: '',
        DOB: '',
        profile: '',
        provinceName: '',
        website: '',
        isAuthenticated: false,
      });
      enqueueSnackbar('Logged out successfully', { variant: 'success' });
      navigate(RoutesLocal.Signin.path);
    } catch (error) {
      enqueueSnackbar(`Error Occured - ${error.message}`, { variant: 'warning' });
    }
  };

  return (
    <div className={`sidebar-shell ${isSidebarOpen ? 'open' : 'closed'}`}>
      <aside id="sidebar" className="sidebar sidebar-panel">
       {/*} <div className="sidebar-brand">
          <div className="sidebar-brand__icon">{userInitials}</div>
          <div className="sidebar-brand__copy">
            <span className="sidebar-brand__eyebrow">Admin Console</span>
            <strong>Archive Dashboard</strong>
            <span className="sidebar-brand__subtext">Fast access to members, content and requests</span>
          </div>
        </div>*/}

        <div className="sidebar-user-card">
          <div>
            <span className="sidebar-user-card__label">Signed in as</span>
            <strong>{userDisplayName}</strong>
           {/* <span className="sidebar-user-card__status">Operational and ready</span> */}
          </div>
          <span className="sidebar-user-card__pulse" aria-hidden="true" />
        </div>

        <ul className="sidebar-nav" id="sidebar-nav">
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link nav-link--home ${isActive ? 'active' : ''}`}
              to={RoutesLocal.DashboardOverview.path}
            >
              <i className="bi bi-speedometer2"></i>
              <span>Dashboard</span>
              <i className="bi bi-arrow-up-right nav-link__chevron"></i>
            </NavLink>
          </li>

          {navigationSections.map((section) => {
            const sectionActive = isSectionActive(section);

            return (
              <li className="nav-item" key={section.id}>
                <button
                  type="button"
                  className={`nav-link nav-link--section ${sectionActive ? '' : 'collapsed'}`}
                  data-bs-target={`#${section.id}`}
                  data-bs-toggle="collapse"
                >
                  <i className={`bi ${section.icon}`}></i>
                  <span>{section.title}</span>
                  <i className="bi bi-chevron-down ms-auto nav-link__chevron"></i>
                </button>

                <ul
                  id={section.id}
                  className={`nav-content collapse ${sectionActive ? 'show' : ''}`}
                  data-bs-parent="#sidebar-nav"
                >
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <NavLink to={link.path} className={({ isActive }) => (isActive ? 'active' : '')}>
                        <i className="bi bi-dot"></i>
                        <span>{link.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>

        <div className="sidebar-footer">
          <button type="button" className="sidebar-signout-btn" onClick={handleSignOut}>
            <i className="bi bi-box-arrow-right"></i>
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </div>
  );
}